import React from 'react';
import { Building2, MessageCircle, GraduationCap, ArrowRight } from 'lucide-react';

const WHATSAPP_SCHOOL = `https://wa.me/60166291687?text=${encodeURIComponent(
  "Hi Kalah, I represent a school/kindergarten and would like to invite you to deliver a workshop for our teachers. Could you share more about your school training options?"
)}`;

const WHATSAPP_WORKSHOP = `https://wa.me/60166291687?text=${encodeURIComponent(
  "Hi Kalah, I'd like to request a workshop quote and availability for our school. Could you share the next available dates?"
)}`;

const ForSchools = () => {
  return (
    <section
      id="for-schools"
      data-testid="for-schools"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-[#1f2a5e] to-[#3B4BA0] text-white relative overflow-hidden"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-teal-400/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-blue-400/10 blur-3xl"
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: pitch */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 text-blue-100 px-4 py-2 rounded-full text-sm font-semibold mb-5">
              <Building2 className="h-4 w-4" />
              For Schools &amp; Kindergartens
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
              Training for Schools &amp; Kindergartens
            </h2>

            <p className="text-blue-100 text-lg leading-relaxed mb-6 max-w-2xl">
              Practical workshops for early childhood educators on phonics,
              phonemic awareness, reading readiness, classroom language,
              storytelling, early writing, and supporting children who struggle
              with reading — designed for your team, delivered at your school.
            </p>

            <ul className="space-y-2.5 text-blue-50 mb-8 max-w-2xl">
              {[
                'Tailored to the age groups your school teaches',
                'Half-day, full-day or modular formats',
                'On-site (Klang Valley) or live online for international schools',
                'Practical demonstrations + lesson-planning support',
                'Certificate of participation for every educator'
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-teal-300 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <a
                href={WHATSAPP_SCHOOL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="invite-kalah-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#3B4BA0] hover:bg-blue-50 font-bold px-6 py-3.5 rounded-xl shadow-lg hover:shadow-2xl transition-all"
              >
                <MessageCircle className="h-5 w-5" />
                Invite Teacher Kalah to Train Your Team
              </a>
              <a
                href={WHATSAPP_WORKSHOP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="request-workshop-btn"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/30 hover:border-white text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-xl transition-all"
              >
                Request a School Workshop
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Right: visual stat / quote */}
          <div className="lg:col-span-5">
            <div className="bg-white/8 backdrop-blur-md border border-white/15 rounded-3xl p-8 lg:p-10 shadow-2xl">
              <GraduationCap className="h-10 w-10 text-teal-300 mb-5" />
              <p className="text-xl sm:text-2xl font-semibold leading-snug mb-5">
                &ldquo;Teachers don&apos;t need more theory. They need clear
                methods that work in a real classroom — and the confidence to
                use them on Monday morning.&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 text-white flex items-center justify-center font-bold">
                  K
                </div>
                <div>
                  <p className="font-bold leading-tight">Kalah K. Singam</p>
                  <p className="text-xs text-blue-200">
                    Phonics Teacher Trainer · Montessorian
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForSchools;
